import {
  DEFAULT_EXPIRY,
  DEFAULT_LANGUAGE,
  DEFAULT_VISIBILITY,
  EXPIRY_OPTIONS,
  MAX_PASTE_BYTES,
  MAX_TITLE_LENGTH,
  VISIBILITIES,
  type ExpiryId,
  type LanguageId,
  type PasteDetail,
  type SessionUser,
  type Visibility,
  type WritePasteResponse,
} from '../../shared/types'
import { ApiError, createPaste, fetchPaste, updatePaste } from '../api'
import { createEditor, type EditorHandle } from '../editor'
import { buildFrame } from '../frame'
import { LANGUAGES, detectLanguage } from '../languages'
import { describeCommand, fetchCommand } from '../oneliner'
import { forgetPalettePastas } from '../palette'
import { internalLink, navigate } from '../router'
import { saucePicker } from '../saucepicker'
import { byteLength, copyText, formatBytes, h, plural, select, toast } from '../ui'

const EXPIRY_STORAGE_KEY = 'pastabin:expiry'
const VISIBILITY_STORAGE_KEY = 'pastabin:visibility'
const IS_MAC = /Mac|iPhone|iPad/.test(navigator.userAgent)
const SUBMIT_HINT = IS_MAC ? '⌘↩' : 'Ctrl+↩'

export interface EditorViewOptions {
  user: SessionUser
  /** Present when editing an existing paste in place. */
  id?: string
}

export function editorView(root: HTMLElement, options: EditorViewOptions): () => void {
  const editing = options.id !== undefined
  const frame = buildFrame({ user: options.user, active: editing ? null : 'new' })

  let language: LanguageId = DEFAULT_LANGUAGE
  let languagePinned = false
  let expiry: ExpiryId = restore<ExpiryId>(EXPIRY_STORAGE_KEY, DEFAULT_EXPIRY, (value) =>
    EXPIRY_OPTIONS.some((option) => option.id === value),
  )
  let visibility: Visibility = restore<Visibility>(VISIBILITY_STORAGE_KEY, DEFAULT_VISIBILITY, (value) =>
    VISIBILITIES.some((option) => option.id === value),
  )
  let burnAfterRead = false
  let linkRow: HTMLElement | null = null
  let saving = false
  let dirty = false
  let disposed = false

  const titleInput = h('input', {
    class: 'title-input',
    type: 'text',
    placeholder: 'Title (optional)',
    maxlength: String(MAX_TITLE_LENGTH),
    spellcheck: 'false',
    'aria-label': 'Title',
    oninput: () => {
      dirty = true
      dismissLink()
      updateStatus()
    },
  })

  const languageField = select(
    {
      'aria-label': 'Language',
      title: 'Syntax highlighting',
      onchange: (event: Event) => {
        language = (event.target as HTMLSelectElement).value as LanguageId
        languagePinned = true
        dirty = true
        editor.setLanguage(language)
      },
    },
    LANGUAGES.map((entry) => ({ value: entry.id, label: entry.label })),
    language,
  )
  const languageSelect = languageField.querySelector('select') as HTMLSelectElement

  const expiryField = select(
    {
      'aria-label': 'Expiry',
      title: 'How long it keeps',
      onchange: (event: Event) => {
        expiry = (event.target as HTMLSelectElement).value as ExpiryId
        remember(EXPIRY_STORAGE_KEY, expiry)
        dirty = true
        updateStatus()
      },
    },
    EXPIRY_OPTIONS.map((option) => ({ value: option.id, label: `${option.label} · ${option.hint}` })),
    expiry,
  )
  const expirySelect = expiryField.querySelector('select') as HTMLSelectElement

  const visibilityField = select(
    {
      'aria-label': 'Visibility',
      title: 'Who can open the link',
      onchange: (event: Event) => {
        visibility = (event.target as HTMLSelectElement).value as Visibility
        remember(VISIBILITY_STORAGE_KEY, visibility)
        dirty = true
        updateStatus()
      },
    },
    VISIBILITIES.map((option) => ({ value: option.id, label: `${option.label} · ${option.hint}` })),
    visibility,
  )
  const visibilitySelect = visibilityField.querySelector('select') as HTMLSelectElement

  const burnButton = h(
    'button',
    {
      class: 'control',
      type: 'button',
      'aria-pressed': 'false',
      title: 'Deleted the first time someone else opens it. Unread copies self-destruct after 30 days.',
      onclick: () => {
        burnAfterRead = !burnAfterRead
        burnButton.setAttribute('aria-pressed', String(burnAfterRead))
        dirty = true
        updateStatus()
      },
    },
    ['one serving'],
  )

  const saveLabel = h('span', {}, [editing ? 'Save' : 'Serve'])
  const saveButton = h(
    'button',
    { class: 'control primary', type: 'button', onclick: () => void save() },
    [saveLabel, h('kbd', {}, [SUBMIT_HINT])],
  )

  frame.controls.append(
    titleInput,
    languageField,
    expiryField,
    visibilityField,
    burnButton,
    saucePicker(() => editor.refreshTheme()),
    saveButton,
  )

  const editorHost = h('div', { class: 'editor' })
  frame.stage.append(editorHost)

  const editor: EditorHandle = createEditor({
    parent: editorHost,
    doc: '',
    language,
    placeholder: `Paste it here. ${SUBMIT_HINT} to ${editing ? 'save' : 'serve'}.`,
    onChange: (doc) => {
      dirty = true
      // A served link belongs to the old text, so retire it on the next keystroke.
      dismissLink()
      if (!languagePinned) {
        const detected = detectLanguage(doc)
        if (detected !== language) {
          language = detected
          languageSelect.value = detected
          editor.setLanguage(detected)
        }
      }
      updateStatus()
    },
    onSubmit: () => void save(),
  })

  if (!editing) {
    frame.statusRight.append(
      h('span', { class: 'hide-sm' }, [`${SUBMIT_HINT} to serve`]),
      h(
        'button',
        {
          class: 'control hide-sm',
          type: 'button',
          style: 'padding:2px 7px;font-size:11px',
          title: 'Copy a curl one-liner (needs an API token)',
          onclick: async () => {
            const copied = await copyText(
              `curl --data-binary @file.txt -H "authorization: bearer \$PASTABIN_TOKEN" ${location.origin}`,
            )
            toast(copied ? 'curl one-liner copied' : 'Could not copy')
          },
        },
        ['curl'],
      ),
    )
  }

  updateStatus()
  root.append(frame.element)
  document.title = editing ? 'edit · pastabin' : 'pastabin'

  if (editing && options.id) {
    void load(options.id)
  } else {
    editor.view.focus()
  }

  async function load(id: string): Promise<void> {
    saveButton.disabled = true
    try {
      const paste = await fetchPaste(id)
      if (disposed) return
      if (!paste.owned) {
        toast("That pasta isn't yours to edit")
        navigate(`/p/${id}`, { replace: true })
        return
      }
      applyLoaded(paste)
    } catch (error) {
      if (disposed) return
      toast(error instanceof ApiError ? error.message : 'Could not open that pasta')
      navigate('/mine', { replace: true })
    }
  }

  function applyLoaded(paste: PasteDetail): void {
    titleInput.value = paste.title ?? ''
    language = paste.language
    languagePinned = true
    languageSelect.value = paste.language
    editor.setLanguage(paste.language)

    visibility = paste.visibility
    visibilitySelect.value = paste.visibility
    burnAfterRead = paste.burnAfterRead
    burnButton.setAttribute('aria-pressed', String(paste.burnAfterRead))

    // Expiry is a duration, and the stored value is an instant — leaving the
    // remembered choice would silently re-clock the paste, so show what's true.
    expiry = paste.expiresAt === null ? 'preserved' : expiry
    expirySelect.value = expiry

    editor.view.dispatch({
      changes: { from: 0, to: editor.view.state.doc.length, insert: paste.content },
    })

    dirty = false
    document.title = `edit ${paste.title ?? paste.id} · pastabin`
    updateStatus()
    editor.view.focus()
  }

  function updateStatus(): void {
    const bytes = byteLength(editor.view.state.doc.toString())
    const oversize = bytes > MAX_PASTE_BYTES
    const parts = [
      formatBytes(bytes),
      plural(editor.view.state.doc.lines, 'line'),
      visibility === 'public' ? 'public link' : 'private',
      burnAfterRead ? `one serving · ${expiryPhrase(expiry)}` : expiryPhrase(expiry),
    ]
    if (oversize) parts.push(`over the ${formatBytes(MAX_PASTE_BYTES)} limit`)
    frame.statusLeft.textContent = parts.join(' · ')
    frame.statusLeft.classList.toggle('warn', oversize)
    saveButton.disabled = saving || oversize || bytes === 0
  }

  async function save(): Promise<void> {
    if (saving) return
    const content = editor.view.state.doc.toString()
    if (!content.trim()) {
      toast('Nothing to serve yet')
      return
    }
    if (byteLength(content) > MAX_PASTE_BYTES) {
      toast(`Too big — ${formatBytes(MAX_PASTE_BYTES)} max`)
      return
    }

    saving = true
    saveButton.disabled = true
    saveLabel.textContent = editing ? 'Saving…' : 'Serving…'

    const payload = {
      content,
      title: titleInput.value.trim() || null,
      language,
      expiry,
      visibility,
      burnAfterRead,
    }

    try {
      if (editing && options.id) {
        await updatePaste(options.id, payload)
        forgetPalettePastas()
        dirty = false
        toast('Saved')
      } else {
        const result = await createPaste(payload)
        forgetPalettePastas()
        const copied = await copyText(result.url)
        showLink(result)
        dirty = false
        toast(copied ? 'Served · link copied' : 'Served')
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        toast('Your session expired — sign in again')
      } else {
        toast(error instanceof ApiError ? error.message : 'Could not save that pasta')
      }
    } finally {
      saving = false
      saveLabel.textContent = editing ? 'Save' : 'Serve'
      updateStatus()
    }
  }

  function showLink(result: WritePasteResponse): void {
    dismissLink()
    const field = h('input', { readonly: true, spellcheck: 'false', value: result.url })
    field.addEventListener('focus', () => field.select())

    linkRow = h('div', { class: 'linkrow' }, [
      field,
      h(
        'button',
        {
          class: 'control',
          type: 'button',
          onclick: async () => {
            const copied = await copyText(result.url)
            toast(copied ? 'Link copied' : 'Could not copy')
          },
        },
        ['Copy'],
      ),
      h('a', { class: 'control', ...internalLink(`/p/${result.id}`) }, ['Open']),
      h('a', { class: 'control', href: result.rawUrl, target: '_blank', rel: 'noopener' }, ['Raw']),
      served(result),
      result.visibility === 'private'
        ? h('span', { class: 'hide-sm muted' }, ['private — only you can open it'])
        : null,
      result.burnAfterRead
        ? h('span', { class: 'hide-sm', style: 'color:var(--danger)' }, ['one serving'])
        : null,
    ])
    frame.insertBelowBar(linkRow)
  }

  /** The fetch-it-back command for the pasta that was just served. */
  function served(result: WritePasteResponse): HTMLElement {
    const command = fetchCommand(
      { id: result.id, title: titleInput.value.trim() || null, language, visibility },
      location.origin,
    )
    return h(
      'button',
      {
        class: 'control',
        type: 'button',
        title: describeCommand(command),
        onclick: async () => {
          const copied = await copyText(command.command)
          toast(copied ? `${command.tool} command copied` : 'Could not copy')
        },
      },
      [command.tool],
    )
  }

  function dismissLink(): void {
    linkRow?.remove()
    linkRow = null
  }

  function beforeUnload(event: BeforeUnloadEvent): void {
    if (dirty) event.preventDefault()
  }

  window.addEventListener('beforeunload', beforeUnload)

  return () => {
    disposed = true
    window.removeEventListener('beforeunload', beforeUnload)
    editor.destroy()
  }
}

/** "keeps 1 day" / "never expires" — the cute names alone don't tell you much. */
function expiryPhrase(id: ExpiryId): string {
  const option = EXPIRY_OPTIONS.find((candidate) => candidate.id === id)
  if (!option) return ''
  return option.seconds === null ? 'never expires' : `keeps ${option.hint}`
}

function restore<T extends string>(key: string, fallback: T, valid: (value: string) => boolean): T {
  try {
    const stored = localStorage.getItem(key)
    if (stored && valid(stored)) return stored as T
  } catch {
    // No storage available; fall through to the default.
  }
  return fallback
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Non-fatal.
  }
}
